import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSeedling,
  faTicketAlt,
  faArrowRight,
  faCrown,
  faLeaf,
  faCheckCircle,
  faPlus,
  faClock,
  faUsers,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

import gardenTourImage from '../../../assets/rose-garden.jpg';
import castleImage from '../../../assets/castle2.png';
import gardensImage from '../../../assets/banner/gardenBan2.webp';

const BookingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  margin: 3rem 0;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StepCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
  border: 3px solid ${props => props.primary ? props.theme.colors.accent : '#e0e0e0'};
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.colors.secondary};
    line-height: 1.6;
  }
`;

const PlusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 50%;
  font-size: 2rem;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    transform: rotate(90deg);
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
`;

const TourDetails = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 3rem;
  
  h3 {
    font-size: 1.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 1.5rem;
  }
`;

const TourMeta = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  align-items: stretch;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const OptionImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const OptionContent = styled.div`
  padding: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
`;

const OptionTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.6rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const OptionDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.5rem;
`;

const BookButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  border: 2px solid ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const UpsellNotice = styled.div`
  background: #f8f9fa;
  border: 2px dashed ${props => props.theme.colors.accent};
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  text-align: center;
  
  p {
    margin: 0;
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    
    svg {
      color: ${props => props.theme.colors.accent};
      margin-right: 0.5rem;
    }
  }
`;

const GardenTour = () => {
  return (
    <BookingContainer>
      <ParallaxBanner 
        title="Seasonal Garden Tour"
        subtitle="Discover the Beauty of Belvoir's Historic Gardens"
        image={gardenTourImage}
      />
      
      <ContentContainer>
        <ProcessSteps>
          <StepCard primary>
            <h3>
              <FontAwesomeIcon icon={faTicketAlt} />
              STEP 1: Choose Entry Ticket
            </h3>
            <p>
              Select either Castle & Gardens entry or Gardens-only entry. 
              The Garden Tour add-on will be available during checkout.
            </p>
          </StepCard>
          
          <PlusIcon>
            <FontAwesomeIcon icon={faPlus} />
          </PlusIcon>
          
          <StepCard>
            <h3>
              <FontAwesomeIcon icon={faSeedling} />
              STEP 2: Add Garden Tour
            </h3>
            <p>
              During the booking process, you'll see the option to add the Seasonal Garden Tour 
              for just £10 per person to your order.
            </p>
          </StepCard>
        </ProcessSteps>

        <SectionTitle>Step 1: Choose Your Entry Ticket</SectionTitle>
        
        <OptionsGrid>
          <OptionCard>
            <OptionImage image={castleImage} />
            <OptionContent>
              <div>
                <OptionTitle>
                  <FontAwesomeIcon icon={faCrown} />
                  Castle & Gardens Entry
                </OptionTitle>
                <OptionDescription>
                  Full access to the Castle State Rooms and Gardens. Perfect for the complete 
                  Belvoir experience including your Seasonal Garden Tour.
                </OptionDescription>
              </div>
              <div>
                <BookButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=6bbc4575-8d31-4cea-9c03-cd28ca2722c2" target="_blank" rel="noopener noreferrer">
                  Book Castle Entry & Add Garden Tour <FontAwesomeIcon icon={faArrowRight} />
                </BookButton>
                <UpsellNotice>
                  <p>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Seasonal Garden Tour (£10pp) available as add-on during booking
                  </p>
                </UpsellNotice>
              </div>
            </OptionContent>
          </OptionCard>
          
          <OptionCard>
            <OptionImage image={gardensImage} />
            <OptionContent>
              <div>
                <OptionTitle>
                  <FontAwesomeIcon icon={faLeaf} />
                  Gardens Entry Only
                </OptionTitle>
                <OptionDescription>
                  Access to the beautiful Gardens and grounds. Perfect for garden enthusiasts 
                  who want to focus on the outdoor spaces and seasonal garden tour.
                </OptionDescription>
              </div>
              <div>
                <BookButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=a1261eab-cef5-424a-8f25-152774aef950" target="_blank" rel="noopener noreferrer">
                  Book Gardens Entry & Add Garden Tour <FontAwesomeIcon icon={faArrowRight} />
                </BookButton>
                <UpsellNotice>
                  <p>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Seasonal Garden Tour (£10pp) available as add-on during booking
                  </p>
                </UpsellNotice>
              </div>
            </OptionContent>
          </OptionCard>
        </OptionsGrid>

        <TourDetails style={{ marginTop: '4rem' }}>
          <h3>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Available Tour Dates
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>24th July</strong><br />
              11:00 AM
            </div>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>30th July</strong><br />
              1:00 PM
            </div>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>7th August</strong><br />
              11:00 AM
            </div>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>13th August</strong><br />
              1:00 PM
            </div>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>21st August</strong><br />
              11:00 AM
            </div>
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <strong>27th August</strong><br />
              1:00 PM
            </div>
          </div>
        </TourDetails>

        <TourDetails>
          <h3>
            <FontAwesomeIcon icon={faSeedling} />
            About the Seasonal Garden Tour
          </h3>
          <p>
            Enjoy a seasonal tour with our head gardener and discover the beauty of our Castle Gardens. 
            Enjoy the Snowdrops early February, Daffodils in Spring & Roses all summer long. 
            Autumn leaves welcome you whilst you explore the secrets of our Garden.
          </p>
          <TourMeta>
            <div>
              <FontAwesomeIcon icon={faUsers} /> Led by our expert head gardener
            </div>
            <div>
              <FontAwesomeIcon icon={faClock} /> Seasonal timing varies
            </div>
          </TourMeta>
        </TourDetails>
      </ContentContainer>
      
      <Footer />
    </BookingContainer>
  );
};

export default GardenTour; 